import React, { useState, useEffect } from 'react';
import { Button, Field, Input, Modal, Note } from '../../../components/app2';
import billingService from '../../../utils/billingService';
import AlertService from '../../../utils/alertService';

/**
 * Alta y edición de un cupón (rediseño jul-2026).
 *
 * Mismos campos, mismas validaciones y el mismo bloqueo de los datos que Stripe
 * no deja cambiar después de crear (código, tipo, descuento y duración): lo que
 * cambia es el molde —modal del sistema, campos con etiqueta y ayuda— y que ese
 * bloqueo ahora se EXPLICA en pantalla en vez de aparecer como campos grises sin
 * motivo.
 *
 * El formulario vive dentro del cuerpo del modal y el botón de guardar lo envía
 * por `form=`, así que el Enter dentro de un campo sigue guardando.
 */

const FORM_ID = 'coupon-form';

const CouponFormModal = ({ existingCoupon, onClose, onSaved }) => {
  const isEdit = !!existingCoupon;
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    amount_off: '',
    percent_off: '',
    duration: 'once',
    duration_in_months: '',
    max_redemptions: '',
    valid_from: '',
    valid_until: '',
    minimum_amount: '',
    first_time_transaction: false,
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (existingCoupon) {
      setForm({
        code: existingCoupon.code || '',
        name: existingCoupon.name || '',
        description: existingCoupon.description || '',
        discount_type: existingCoupon.discount_type || 'percentage',
        amount_off: existingCoupon.amount_off ?? '',
        percent_off: existingCoupon.percent_off ?? '',
        duration: existingCoupon.duration || 'once',
        duration_in_months: existingCoupon.duration_in_months || '',
        max_redemptions: existingCoupon.max_redemptions ?? '',
        valid_from: existingCoupon.valid_from ? existingCoupon.valid_from.slice(0, 10) : '',
        valid_until: existingCoupon.valid_until ? existingCoupon.valid_until.slice(0, 10) : '',
        minimum_amount: existingCoupon.minimum_amount ?? '',
        first_time_transaction: !!existingCoupon.first_time_transaction,
        is_active: existingCoupon.is_active !== false,
      });
    }
  }, [existingCoupon]);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.code.trim()) {
      AlertService?.error('Código requerido', 'Ingresa un código para el cupón.');
      return;
    }
    if (form.discount_type === 'percentage') {
      const p = Number(form.percent_off);
      if (!p || p <= 0 || p > 100) {
        AlertService?.error('Porcentaje inválido', 'Debe estar entre 1 y 100.');
        return;
      }
    }
    if (form.discount_type === 'fixed_amount') {
      const a = Number(form.amount_off);
      if (!a || a <= 0) {
        AlertService?.error('Monto inválido', 'Debe ser mayor a 0.');
        return;
      }
    }
    if (form.duration === 'repeating' && !Number(form.duration_in_months)) {
      AlertService?.error('Meses requeridos', 'Indica el número de meses.');
      return;
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim() || null,
      description: form.description.trim() || null,
      discount_type: form.discount_type,
      amount_off: form.discount_type === 'fixed_amount' ? Number(form.amount_off) : null,
      percent_off: form.discount_type === 'percentage' ? Number(form.percent_off) : null,
      duration: form.duration,
      duration_in_months:
        form.duration === 'repeating' ? Number(form.duration_in_months) : null,
      max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      minimum_amount: form.minimum_amount ? Number(form.minimum_amount) : null,
      first_time_transaction: !!form.first_time_transaction,
      is_active: !!form.is_active,
    };

    try {
      setSubmitting(true);
      let saved;
      if (isEdit) {
        saved = await billingService.admin.updateCoupon(existingCoupon.id, payload);
      } else {
        saved = await billingService.admin.createCoupon(payload);
      }
      AlertService?.success(
        isEdit ? 'Cupón actualizado' : 'Cupón creado',
        'Se sincronizó con Stripe automáticamente.',
        { autoClose: true },
      );
      onSaved?.(saved);
    } catch (err) {
      AlertService?.error(
        'Error',
        err?.response?.data?.error?.message || err?.message || 'Error desconocido',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Editar cupón ${existingCoupon.code}` : 'Nuevo cupón'}
      subtitle={isEdit ? 'Los cambios se sincronizan con Stripe' : 'Se creará también en Stripe'}
      maxWidth={720}
      footer={(
        <>
          <Button variant="outline" size="sm" type="button" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button size="sm" type="submit" form={FORM_ID} icon="check" loading={submitting}>
            {isEdit ? 'Guardar cambios' : 'Crear cupón'}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        {isEdit ? (
          <Note tone="info" style={{ marginBottom: 18 }}>
            Stripe no permite cambiar el código, el tipo de descuento, su valor ni la duración de un
            cupón que ya existe. Para modificarlos hay que crear uno nuevo.
          </Note>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
          <Field
            label="Código *"
            htmlFor="coupon-code"
            hint={isEdit ? undefined : 'Es lo que teclea el cliente. Se guarda en mayúsculas.'}
            style={{ gridColumn: '1 / -1' }}
          >
            <Input
              id="coupon-code"
              mono
              value={form.code}
              onChange={(e) => updateField('code', e.target.value.toUpperCase())}
              placeholder="EJ: PROMO2026"
              disabled={isEdit}
            />
          </Field>

          <Field label="Nombre interno" htmlFor="coupon-name" hint="Solo lo ve el equipo.">
            <Input
              id="coupon-name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Ej: Black Friday 2026"
            />
          </Field>

          <Field label="Descripción" htmlFor="coupon-description" hint="Notas internas.">
            <Input
              id="coupon-description"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Para qué se creó"
            />
          </Field>

          <Field label="Tipo de descuento *" htmlFor="coupon-discount-type">
            <select
              id="coupon-discount-type"
              className="a2-input"
              value={form.discount_type}
              onChange={(e) => updateField('discount_type', e.target.value)}
              disabled={isEdit}
            >
              <option value="percentage">Porcentaje</option>
              <option value="fixed_amount">Monto fijo (MXN)</option>
            </select>
          </Field>

          {form.discount_type === 'percentage' ? (
            <Field label="% de descuento *" htmlFor="coupon-percent">
              <Input
                id="coupon-percent"
                type="number"
                min="1"
                max="100"
                value={form.percent_off}
                onChange={(e) => updateField('percent_off', e.target.value)}
                placeholder="20"
                disabled={isEdit}
              />
            </Field>
          ) : (
            <Field label="Monto MXN *" htmlFor="coupon-amount">
              <Input
                id="coupon-amount"
                type="number"
                min="0"
                step="0.01"
                value={form.amount_off}
                onChange={(e) => updateField('amount_off', e.target.value)}
                placeholder="100"
                disabled={isEdit}
              />
            </Field>
          )}

          <Field label="Duración *" htmlFor="coupon-duration">
            <select
              id="coupon-duration"
              className="a2-input"
              value={form.duration}
              onChange={(e) => updateField('duration', e.target.value)}
              disabled={isEdit}
            >
              <option value="once">Una sola vez</option>
              <option value="repeating">Repetir N meses</option>
              <option value="forever">Permanente</option>
            </select>
          </Field>

          {form.duration === 'repeating' ? (
            <Field label="Meses *" htmlFor="coupon-months">
              <Input
                id="coupon-months"
                type="number"
                min="1"
                value={form.duration_in_months}
                onChange={(e) => updateField('duration_in_months', e.target.value)}
                placeholder="3"
                disabled={isEdit}
              />
            </Field>
          ) : null}

          <Field label="Máximo de canjes" htmlFor="coupon-max" hint="Vacío = sin límite.">
            <Input
              id="coupon-max"
              type="number"
              min="1"
              value={form.max_redemptions}
              onChange={(e) => updateField('max_redemptions', e.target.value)}
              placeholder="Sin límite"
            />
          </Field>

          <Field label="Monto mínimo MXN" htmlFor="coupon-min" hint="Vacío = sin mínimo.">
            <Input
              id="coupon-min"
              type="number"
              min="0"
              step="0.01"
              value={form.minimum_amount}
              onChange={(e) => updateField('minimum_amount', e.target.value)}
              placeholder="Sin mínimo"
            />
          </Field>

          <Field label="Válido desde" htmlFor="coupon-from">
            <Input
              id="coupon-from"
              type="date"
              value={form.valid_from}
              onChange={(e) => updateField('valid_from', e.target.value)}
            />
          </Field>

          <Field label="Válido hasta" htmlFor="coupon-until">
            <Input
              id="coupon-until"
              type="date"
              value={form.valid_until}
              onChange={(e) => updateField('valid_until', e.target.value)}
            />
          </Field>
        </div>

        <div
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 18, paddingTop: 16,
            borderTop: '1px solid var(--line)',
          }}
        >
          <label className="a2-check">
            <input
              type="checkbox"
              checked={form.first_time_transaction}
              onChange={(e) => updateField('first_time_transaction', e.target.checked)}
            />
            Solo en la primera compra del cliente
          </label>

          <label className="a2-check">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
            />
            Activo (se puede canjear)
          </label>
        </div>
      </form>
    </Modal>
  );
};

export default CouponFormModal;
